import { ROLES } from './roleUtils';

export const CHAT_ROLES = ROLES;

export const CHAT_ADMIN_CIRCLE = new Set([
  ROLES.SUPER_ADMIN,
  ROLES.SYSTEM_ADMIN,
  ROLES.PRACTICE_ADMIN,
  ROLES.SYSTEM_CAREGIVER,
]);

/** Roles that may open Messages from the sidebar */
export const CHAT_SIDEBAR_ROLE_IDS = new Set([
  ROLES.SUPER_ADMIN,
  ROLES.SYSTEM_ADMIN,
  ROLES.PRACTICE_ADMIN,
  ROLES.PROVIDER,
  ROLES.CAREGIVER,
  ROLES.PATIENT,
  ROLES.SYSTEM_CAREGIVER,
]);

export const CHAT_ACCESS_MANAGER_ROLE_IDS = new Set([
  ROLES.SUPER_ADMIN,
  ROLES.SYSTEM_ADMIN,
  ROLES.PRACTICE_ADMIN,
]);

export const CHAT_PAIR_TYPES = {
  CAREGIVER_PATIENT: 'caregiver_patient',
  PROVIDER_PATIENT: 'provider_patient',
  CAREGIVER_PROVIDER: 'caregiver_provider',
};

export function canRoleSendChatMessages(roleId) {
  const n = Number(roleId);
  return Number.isFinite(n) && n > 0;
}

export function canManageChatAccess(roleId) {
  return CHAT_ACCESS_MANAGER_ROLE_IDS.has(Number(roleId));
}

export function hasChatSidebarAccess(roleId) {
  return CHAT_SIDEBAR_ROLE_IDS.has(Number(roleId));
}

/** Role-level matrix (Layer 1) — mirrors backend/utils/chatPermissions.js */
export function getAllowedRecipientRoleIds(senderRoleId) {
  const sender = Number(senderRoleId);
  if (!canRoleSendChatMessages(sender)) return [];

  switch (sender) {
    case ROLES.SUPER_ADMIN:
      return [
        ROLES.SYSTEM_ADMIN,
        ROLES.PRACTICE_ADMIN,
        ROLES.SYSTEM_CAREGIVER,
      ];
    case ROLES.SYSTEM_ADMIN:
      return [
        ROLES.SUPER_ADMIN,
        ROLES.SYSTEM_ADMIN,
        ROLES.PRACTICE_ADMIN,
        ROLES.SYSTEM_CAREGIVER,
        ROLES.PROVIDER,
        ROLES.CAREGIVER,
      ];
    case ROLES.PRACTICE_ADMIN:
      return [
        ROLES.SUPER_ADMIN,
        ROLES.SYSTEM_ADMIN,
        ROLES.SYSTEM_CAREGIVER,
        ROLES.PROVIDER,
        ROLES.CAREGIVER,
        ROLES.PATIENT,
      ];
    case ROLES.SYSTEM_CAREGIVER:
      return [
        ROLES.SUPER_ADMIN,
        ROLES.SYSTEM_ADMIN,
        ROLES.PRACTICE_ADMIN,
        ROLES.PATIENT,
      ];
    case ROLES.PROVIDER:
      return [ROLES.PRACTICE_ADMIN, ROLES.CAREGIVER, ROLES.PATIENT];
    case ROLES.CAREGIVER:
      return [ROLES.PRACTICE_ADMIN, ROLES.PROVIDER, ROLES.PATIENT];
    case ROLES.PATIENT:
      return [ROLES.PROVIDER, ROLES.CAREGIVER, ROLES.SYSTEM_CAREGIVER];
    default:
      return [];
  }
}

export function canSendMessageToRole(senderRoleId, recipientRoleId) {
  const allowed = getAllowedRecipientRoleIds(senderRoleId);
  const recipient = Number(recipientRoleId);
  return Number.isFinite(recipient) && allowed.includes(recipient);
}

export function resolveChatPairType(roleA, roleB) {
  const a = Number(roleA);
  const b = Number(roleB);
  const set = new Set([a, b]);
  if (set.has(ROLES.CAREGIVER) && set.has(ROLES.PATIENT)) return CHAT_PAIR_TYPES.CAREGIVER_PATIENT;
  if (set.has(ROLES.PROVIDER) && set.has(ROLES.PATIENT)) return CHAT_PAIR_TYPES.PROVIDER_PATIENT;
  if (set.has(ROLES.CAREGIVER) && set.has(ROLES.PROVIDER)) return CHAT_PAIR_TYPES.CAREGIVER_PROVIDER;
  return null;
}

/** Display order when picking someone to message */
export const CHAT_RECIPIENT_ROLE_GROUPS = [
  { roleId: ROLES.SUPER_ADMIN, label: 'Super Admins', tabLabel: 'Super' },
  { roleId: ROLES.SYSTEM_ADMIN, label: 'System Admins', tabLabel: 'Sys Admin' },
  { roleId: ROLES.PRACTICE_ADMIN, label: 'Practice Admins', tabLabel: 'Practice Admins' },
  { roleId: ROLES.PROVIDER, label: 'Providers', tabLabel: 'Providers' },
  { roleId: ROLES.CAREGIVER, label: 'Caregivers', tabLabel: 'Caregivers' },
  { roleId: ROLES.SYSTEM_CAREGIVER, label: 'System Caregivers', tabLabel: 'System Care' },
  { roleId: ROLES.PATIENT, label: 'Patients', tabLabel: 'Patients' },
];

export function getChatRoleDisplay(roleId, roleNameFallback = '') {
  const id = Number(roleId);
  const labels = {
    [ROLES.SUPER_ADMIN]: 'Super Admin',
    [ROLES.SYSTEM_ADMIN]: 'System Admin',
    [ROLES.PRACTICE_ADMIN]: 'Practice Admin',
    [ROLES.PROVIDER]: 'Provider',
    [ROLES.CAREGIVER]: 'Caregiver',
    [ROLES.PATIENT]: 'Patient',
    [ROLES.SYSTEM_CAREGIVER]: 'System Caregiver',
  };
  if (labels[id]) return labels[id];
  const fallback = String(roleNameFallback || '').trim();
  return fallback || 'User';
}

export function getChatRoleBadgeClass(roleId) {
  switch (Number(roleId)) {
    case ROLES.SUPER_ADMIN:
      return 'chat-role-badge--super-admin';
    case ROLES.SYSTEM_ADMIN:
      return 'chat-role-badge--system-admin';
    case ROLES.PRACTICE_ADMIN:
      return 'chat-role-badge--practice-admin';
    case ROLES.PROVIDER:
      return 'chat-role-badge--provider';
    case ROLES.CAREGIVER:
      return 'chat-role-badge--caregiver';
    case ROLES.PATIENT:
      return 'chat-role-badge--patient';
    case ROLES.SYSTEM_CAREGIVER:
      return 'chat-role-badge--system-caregiver';
    default:
      return 'chat-role-badge--default';
  }
}

export function groupUsersForChatPicker(users = []) {
  const buckets = new Map(
    CHAT_RECIPIENT_ROLE_GROUPS.map((g) => [g.roleId, []])
  );
  const other = [];

  users.forEach((user) => {
    const roleId = Number(user.role_id);
    if (buckets.has(roleId)) {
      buckets.get(roleId).push(user);
    } else {
      other.push(user);
    }
  });

  const groups = CHAT_RECIPIENT_ROLE_GROUPS.map((g) => ({
    ...g,
    users: buckets.get(g.roleId) || [],
  })).filter((g) => g.users.length > 0);

  if (other.length > 0) {
    groups.push({ roleId: null, label: 'Other', users: other });
  }

  return groups;
}

export function buildChatRecipientRoleTabs(users = []) {
  const grouped = groupUsersForChatPicker(users);
  const tabs = [
    { id: 'all', label: 'All', count: users.length, roleId: null },
  ];
  grouped.forEach((group) => {
    if (group.roleId == null) {
      tabs.push({
        id: 'other',
        label: 'Other',
        count: group.users.length,
        roleId: null,
      });
      return;
    }
    const meta = CHAT_RECIPIENT_ROLE_GROUPS.find((g) => g.roleId === group.roleId);
    tabs.push({
      id: String(group.roleId),
      label: meta?.tabLabel || group.label,
      count: group.users.length,
      roleId: group.roleId,
    });
  });
  return tabs;
}
